import {
  Chart,
  ChartData,
  ChartDataset,
  ChartEvent,
  ChartOptions,
  LegendElement,
  LegendItem,
} from 'chart.js';
import React from 'react';

import {EXPANDED_GRAPH_FONT_SIZE, GRAPH_FONT_SIZE} from 'app/components/AnalyzePolygonChart/utils';
import {LARGE_CHART_HEIGHT, SMALL_CHART_HEIGHT} from 'app/components/Dashboard/utils/chartUtils';
import {MultifeatureAnalysisConfig} from 'app/src/components/Dashboard/types';
import * as chartUtils from 'app/src/components/Dashboard/utils/chartUtils';

export type GenericBarChartProps = {
  widgetConfig: MultifeatureAnalysisConfig;
  isExpanded: boolean;
  unit?: string; // used in tooltip
  showPercentage?: boolean;
  chartData: ChartDataset<'bar', number[]>[];
  allDates: string[];
  allowAnimation?: boolean;
  alwaysShowLegend?: boolean;
  onLegendClick?: (event: ChartEvent, legendItem: LegendItem, legend: LegendElement<'bar'>) => void;
};

export const GenericBarChart: React.FC<GenericBarChartProps> = ({
  widgetConfig,
  isExpanded,
  unit,
  showPercentage = false,
  chartData,
  allDates,
  allowAnimation = true,
  alwaysShowLegend = false, // useful for forcing the legend for user selected features so that downloads have legend
  onLegendClick,
}) => {
  const chartRef = React.useRef<Chart | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  // Function to generate chart data and options
  const getChartComposition = React.useCallback(() => {
    const fontSize = isExpanded ? EXPANDED_GRAPH_FONT_SIZE : GRAPH_FONT_SIZE;

    const data: ChartData<'bar'> = {
      labels: allDates.map((date) =>
        chartUtils.formatAggregatedDate(date, widgetConfig.timeAggregation)
      ),
      datasets: chartData,
    };

    const options: ChartOptions<'bar'> = {
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: fontSize,
            },
          },
        },
        y: {
          stacked: true,
          border: {
            display: false,
          },
          ticks: {
            maxTicksLimit: 5,
            callback: function (value) {
              return showPercentage ? `${value}%` : value;
            },
            font: {
              size: fontSize,
            },
          },
          max: showPercentage ? 100 : undefined,
        },
      },
      plugins: {
        legend: {
          display: alwaysShowLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'rectRounded',
            padding: 20,
            font: {
              size: fontSize,
            },
          },
          // disable legend click that modifies chart.
          onClick: (event, legendItem, legend) => {
            if (onLegendClick) {
              onLegendClick(event, legendItem, legend);
              return;
            }
            // This is the default behavior for the legend
            // https://www.chartjs.org/docs/latest/configuration/legend.html#custom-on-click-actions
            const index = legendItem.datasetIndex!;
            const ci = legend.chart;
            if (ci.isDatasetVisible(index)) {
              ci.hide(index);
              legendItem.hidden = true;
            } else {
              ci.show(index);
              legendItem.hidden = false;
            }
          },
        },
        tooltip: {
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0]?.dataset?.label || '';
            },
            label: (tooltipItem) => {
              return `${tooltipItem.label}: ${tooltipItem.formattedValue} ${showPercentage ? '%' : unit}`;
            },
          },
          titleFont: {
            size: fontSize,
          },
          bodyFont: {
            size: fontSize,
          },
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      animation: allowAnimation ? {duration: 400} : false, // 400 is default for chartjs
    };

    return {data, options};
  }, [
    isExpanded,
    allDates,
    chartData,
    showPercentage,
    alwaysShowLegend,
    allowAnimation,
    widgetConfig.timeAggregation,
    onLegendClick,
    unit,
  ]);

  const initialConfigRef = React.useRef<ReturnType<typeof getChartComposition>>();
  if (!initialConfigRef.current) {
    initialConfigRef.current = getChartComposition();
  }

  // MOUNT / UNMOUNT only, then destroy on unmount
  React.useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const {data, options} = initialConfigRef.current!;
    chartRef.current = new Chart(ctx, {type: 'bar', data, options});

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [canvasRef, initialConfigRef]);

  // UPDATE in place whenever config changes
  React.useEffect(() => {
    if (!chartRef.current) return;
    const {data, options} = getChartComposition();
    chartRef.current.data = data;
    chartRef.current.options = options;
    chartRef.current.update();
  }, [getChartComposition]);

  return (
    <div style={{height: isExpanded ? LARGE_CHART_HEIGHT : SMALL_CHART_HEIGHT, width: '100%'}}>
      <canvas ref={canvasRef} />
    </div>
  );
};
